import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AuthCallback component handles the session_id from Emergent Google Auth.
 * This component processes the authentication callback silently and redirects to admin dashboard.
 */
const AuthCallback = ({ setUser }) => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error('[AuthCallback] No session_id found in URL');
          toast.error('Authentication failed - no session ID');
          navigate('/admin-login');
          return;
        }

        const sessionId = sessionIdMatch[1];
        console.log('[AuthCallback] Processing session_id:', sessionId.substring(0, 10) + '...');

        // Call Emergent auth endpoint to get user data
        const response = await axios.get(
          'https://demobackend.emergentagent.com/auth/v1/env/oauth/session-data',
          {
            headers: { 'X-Session-ID': sessionId }
          }
        );

        const googleUserInfo = response.data;
        console.log('[AuthCallback] Got user info:', googleUserInfo.email);

        // Send to our backend to create/link admin account
        const loginResponse = await axios.post(`${API}/auth/admin/google-login`, {
          google_user_info: googleUserInfo
        });

        if (loginResponse.data.success) {
          // Store token and user data
          localStorage.setItem('authToken', loginResponse.data.token);
          
          const userData = {
            ...loginResponse.data.user,
            role: 'admin',
            token: loginResponse.data.token
          };
          
          setUser(userData);
          toast.success(`Welcome, ${loginResponse.data.user.business_name || googleUserInfo.name}!`);
          
          // Redirect to admin dashboard (clear the hash)
          navigate('/admin', { replace: true });
        } else {
          throw new Error(loginResponse.data.error || 'Login failed');
        }
      } catch (error) {
        console.error('[AuthCallback] Error:', error);
        
        // Show specific error messages
        const errorMsg = error.response?.data?.detail || error.message || 'Authentication failed';
        
        if (errorMsg.includes('not linked') || errorMsg.includes('No client found')) {
          toast.error('No account linked to this Google email. Please contact your administrator.');
        } else {
          toast.error(errorMsg);
        }
        
        navigate('/admin-login');
      }
    };

    processAuth();
  }, [navigate, setUser]);

  // Show loading while processing
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-secondary">Signing in with Google...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
